import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";

export type ConfirmationDialogProps = Props;
export type Props = {
  title: string;
  text: string | React.ReactNode;
  onCancel?: () => void;
  onOk: () => void;
  onClose: () => void;
  okText?: string;
  cancelText?: string;
  typeToConfirm?: boolean;
  okButtonColor?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
};

export const ConfirmationDialog: React.FC<Props> = ({
  title,
  text,
  okText,
  cancelText,
  onCancel,
  onOk,
  onClose,
  typeToConfirm,
  okButtonColor = "success",
}) => {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = React.useState<string>("");

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      onOk();
      onClose();
    }
  };

  return (
    <Dialog open onKeyDown={handleKeyDown} sx={{ zIndex: 9600 }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{text}</DialogContentText>
        <br />
        {typeToConfirm && (
          <Stack direction="column" spacing={1}>
            <DialogContentText>
              {t('dialogs.confirmation.typeToConfirm')}
            </DialogContentText>
            <TextField
              fullWidth
              size="small"
              placeholder={t('dialogs.confirmation.deletePlaceholder')}
              onChange={(evt) => setConfirmText(evt.target.value)}
              value={confirmText}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {Boolean(onCancel) && (
          <Button
            onClick={() => {
              onCancel();
              onClose();
            }}
          >
            {cancelText ?? t('dialogs.confirmation.defaultCancel')}
          </Button>
        )}
        <Button
          variant="contained"
          color={okButtonColor}
          disabled={typeToConfirm && confirmText !== "delete"}
          onClick={() => {
            onOk();
            onClose();
          }}
        >
          {okText ?? t('dialogs.confirmation.defaultOk')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
